import React, { createContext, useState, useContext, useEffect } from 'react';
import { api } from '../utils/authApi';

const ConcernContext = createContext();

export const useConcerns = () => {
  const context = useContext(ConcernContext);
  if (!context) {
    throw new Error('useConcerns must be used within ConcernProvider');
  }
  return context;
};

export const ConcernProvider = ({ children }) => {
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // Fetch concerns from API
  useEffect(() => {
    const fetchConcerns = async () => {
      try {
        setLoading(true);
        
        const projectString = localStorage.getItem('selectedProject');
        const userInfoString = localStorage.getItem('user_info');

        if (!projectString || !userInfoString) {
          setConcerns([]);
          setLoading(false);
          return;
        }

        const projectID = JSON.parse(projectString).projectID;
        const userInfo = JSON.parse(userInfoString);
        const userID = userInfo.user_id;

        // Update current project ID
        setCurrentProjectId(projectID);

        // Use authenticated API call
        const response = await api.get(`/concerns/project/${projectID}`);
        console.log("IN CONCERN CONTEXT",response);
        if (!response.ok) {
          throw new Error('Failed to fetch concerns');
        }

        const data = await response.json();


        if (data.Message) {
          // Transform array response to objects
          const transformedConcerns = data.Message.map(concern => {
          
            return {
              id: concern[0],
              clientId: concern[1],
              projectId: concern[2],
              title: concern[3],
              category: concern[4],
              priority: concern[5],
              description: concern[6],
              location: concern[7],
              status: concern[8],
              engineer_note: concern[9],
              engineer_email: concern[10],
              resolution: concern[11],
              submittedDate: concern[12],
              resolvedDate: concern[13]
            };
          });
          
          setConcerns(transformedConcerns);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching concerns:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchConcerns();
  }, [currentProjectId]); // Re-run when project changes

  const addConcern = async (concern) => {
    try {
      // Get user and project info
      const userInfoString = localStorage.getItem('user_info');
      const projectString = localStorage.getItem('selectedProject');
      
      if (!userInfoString || !projectString) {
        setError('User or project not found');
        return { success: false, error: 'User or project not found' };
      }

      const userInfo = JSON.parse(userInfoString);
      const userID = userInfo.user_id;
      const projectID = JSON.parse(projectString).projectID;
      const projectLocation = JSON.parse(projectString).projectLocation;

      // Prepare the concern data matching your backend schema
      const concernData = {
        project_id: projectID,
        project_name: "Project Name", // You might want to store this in selectedProject
        title: concern.title,
        category: concern.category,
        priority: concern.priority,
        description: concern.description,
        location: projectLocation,
      };

      console.log('Sending concern to API:', concernData);

      // POST request to add concern (JWT automatically included, user_id from token)
      const response = await api.post('/concerns/add', concernData);

      if (!response.ok) {
        throw new Error('Failed to add concern');
      }

      const data = await response.json();
      console.log('Backend response:', data);

      if (data.Message === "Concern added successfully") {
        // Refetch all concerns to get the updated list from database
        const fetchResponse = await api.get(`/concerns/project/${projectID}`);
        const fetchData = await fetchResponse.json();
        
        // Update concerns state with fresh data from database
        if (fetchData.Message) {
          const transformedConcerns = fetchData.Message.map(concern => {
            const submittedDate = concern[15] 
              ? new Date(concern[15]).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })
              : null;
            
            const resolvedDate = concern[16]
              ? new Date(concern[16]).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })
              : null;
            
            return {
              id: concern[0],
              clientId: concern[1],
              projectId: concern[2],
              title: concern[3],
              category: concern[4],
              priority: concern[5],
              description: concern[6],
              location: concern[7],
              status: concern[8],
              engineer_note: concern[9],
              engineer_email: concern[10],
              resolution: concern[11],
              submittedDate: concern[12],
              resolvedDate: concern[13]
            };
          });
          setConcerns(transformedConcerns);
        }

        return { success: true, message: 'Concern added successfully' };
      } else {
        throw new Error(data.Message || 'Failed to add concern');
      }

    } catch (err) {
      console.error('Error adding concern:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const deleteConcern = async (id) => {
    try {
      console.log('Deleting concern with ID:', id);

      // DELETE request to remove concern (JWT automatically included)
      const response = await api.delete(`/concerns/delete/${id}`);

      if (!response.ok) {
        throw new Error('Failed to delete concern');
      }

      const data = await response.json();
      console.log('Delete response:', data);

      if (data.Message === "Concern deleted successfully") {
        // Remove from local state immediately for instant UI update
        setConcerns(concerns.filter(c => c.id !== id));
        return { success: true, message: 'Concern deleted successfully' };
      } else if (data.Message === "Concern not found") {
        // Concern doesn't exist in database, but remove from local state anyway
        setConcerns(concerns.filter(c => c.id !== id));
        return { success: false, error: 'Concern not found' };
      } else {
        throw new Error(data.Message || 'Failed to delete concern');
      }

    } catch (err) {
      console.error('Error deleting concern:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Function to manually trigger refetch when project changes
  const changeProject = (projectId) => {
    setCurrentProjectId(projectId);
  };

  return (
    <ConcernContext.Provider 
      value={{ 
        concerns, 
        addConcern, 
        deleteConcern, 
        loading, 
        error,
        changeProject
      }}
    >
      {children}
    </ConcernContext.Provider>
  );
};